import { Text } from "@ttc3k/trekker";
import { Flex } from "styled-system/jsx";
import { useTranslation } from "react-i18next";
import {
  ControlledInput,
  ControlledInputProps,
} from "shared/components/formControl/ControlledInput";
import { EnumEventLocationType } from "gql/generated";
import { ControlledAddressAutofill } from "shared/components/formControl/ControlledAddressAutofill";
import { FieldValues, Path, useFormContext, useWatch } from "react-hook-form";
import { SetStateAction } from "react";
import { Dispatch } from "react";
import { normalizeUrl } from "shared/utils/urlNormalizers";
import { EventVenue } from "../EventVenue";
import { EventCreateEditFormValues } from "../EventCreateEditModal/useEventCreateEditForm";

type HostLocationSelectionProps<T extends FieldValues> = {
  index: number;
  formFieldRoot: Path<T>;
  eventLinkControlledInputProps: ControlledInputProps;
  emailControlledInputProps: ControlledInputProps;
  phoneControlledInputProps: ControlledInputProps;
  setIsEmpty?: Dispatch<SetStateAction<boolean>>;
};

export const HostLocationSelection = <T extends FieldValues>({
  index = 0,
  formFieldRoot,
  eventLinkControlledInputProps,
  emailControlledInputProps,
  phoneControlledInputProps,
  setIsEmpty,
}: HostLocationSelectionProps<T>) => {
  const { t } = useTranslation("events", {
    keyPrefix: "APP.MODAL.EVENT_HOST.LOCATION_SELECTION",
  });

  const { control } = useFormContext<EventCreateEditFormValues>();
  const locationType = useWatch({ control, name: "locationType" });

  return (
    <Flex flexDir="column" gap="400">
      <Text visual="bodyRegular" color="text.mid">
        {t("DESCRIPTION")}
      </Text>

      <Flex flexDir="column" gap="200">
        <EventVenue
          allowCustom={true}
          index={index}
          isEventOrganizer={false}
          label={t("BUSINESS_NAME")}
          formFieldRoot={formFieldRoot}
          setIsEmpty={setIsEmpty}
        />

        {/* Event link */}
        <ControlledInput
          {...eventLinkControlledInputProps}
          type="url"
          normalizer={normalizeUrl}
        />

        {/* Event address */}
        {locationType === EnumEventLocationType.InPerson && (
          <ControlledAddressAutofill
            addressInputName={`${formFieldRoot}.contact.fullAddress`}
            addressObjInputName={`${formFieldRoot}.contact.address`}
            coordsInputName={`${formFieldRoot}.contact.address.location.coordinates`}
            lngInputName={`${formFieldRoot}.contact.address.location.coordinates.lng`}
            latInputName={`${formFieldRoot}.contact.address.location.coordinates.lat`}
          />
        )}

        {/* Email and Phone */}
        <Text visual="bodyRegular" color="text.mid" mt="100">
          {t("EMAIL_AND_PHONE_HINT")}
        </Text>
        <Flex
          flexDir="row"
          justifyContent="space-between"
          alignItems="flex-start"
          gap="200"
        >
          <ControlledInput {...emailControlledInputProps} />
          <ControlledInput {...phoneControlledInputProps} />
        </Flex>
      </Flex>
    </Flex>
  );
};
